/**
 * Rate card upload/download (.xlsx, .xls or .csv) for the Rate Card Matrix.
 * Sheets are recognised by their header row, so a CSV holding only one table works too:
 *   Zones        : Zone | Rate | Amazon Zone (customer only, optional)   — blank Rate = zone not serviced
 *   FSA          : FSA | Zone                                             — customer only
 *   Accessorials : Type (weight|dims) | Code | Label | Min | Mode (replace|add) | Amount
 *                  Min is lb for weight, longest side in inches for dims. Drivers: weight rows only.
 */
import * as XLSX from 'xlsx';

const norm = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const num = (v) => (v === '' || v == null ? null : Number(String(v).replace(/[$,\s]/g, '')));

const col = (headers, ...names) => headers.findIndex((h) => names.includes(h));

const readTable = (sheet) => {
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });
  const headerAt = rows.findIndex((r) => r.filter((c) => String(c).trim()).length >= 2);
  if (headerAt < 0) return null;
  return { headers: rows[headerAt].map(norm), rows: rows.slice(headerAt + 1).filter((r) => r.some((c) => String(c).trim())) };
};

/** Returns { patch, summary[], warnings[] }; `patch` is merged over the current card by the caller. */
export const parseRateCardFile = async (file, kind) => {
  const wb = XLSX.read(await file.arrayBuffer());
  const patch = {};
  const summary = [];
  const warnings = [];

  wb.SheetNames.forEach((name) => {
    const t = readTable(wb.Sheets[name]);
    if (!t) return;
    const h = t.headers;
    const iFsa = col(h, 'fsa', 'postalprefix', 'postalcode');
    const iZone = col(h, 'zone', 'ratezone');
    const iRate = col(h, 'rate', 'rateperpiece', 'price', 'perpiece');
    const iType = col(h, 'type');
    const iMin = col(h, 'min', 'minlb', 'minin', 'minimum', 'threshold');
    const iAmount = col(h, 'amount', 'fee', 'rate');

    if (iFsa >= 0 && iZone >= 0 && kind === 'customer') {
      const fsaZone = {};
      t.rows.forEach((r, i) => {
        const fsa = String(r[iFsa]).replace(/\s/g, '').toUpperCase().slice(0, 3);
        const zone = num(r[iZone]);
        if (/^[A-Z]\d[A-Z]$/.test(fsa) && zone) fsaZone[fsa] = zone;
        else warnings.push(`${name} row ${i + 2}: skipped "${r[iFsa]}" → "${r[iZone]}"`);
      });
      patch.fsaZone = fsaZone;
      summary.push(`${Object.keys(fsaZone).length} FSAs`);
    } else if (iZone >= 0 && iRate >= 0 && iMin < 0) {
      const iAmz = col(h, 'amazonzone');
      const zones = {};
      t.rows.forEach((r) => {
        const zone = num(r[iZone]);
        if (!zone) return;
        const rate = num(r[iRate]);
        zones[zone] = kind === 'customer' ? { rate, amazonZone: iAmz >= 0 ? num(r[iAmz]) : null } : rate;
      });
      patch.zones = zones;
      summary.push(`${Object.keys(zones).length} zone rates`);
    } else if (iMin >= 0 && iAmount >= 0) {
      const iCode = col(h, 'code');
      const iLabel = col(h, 'label', 'description', 'name');
      const iMode = col(h, 'mode');
      const weightTiers = [];
      const dimsTiers = [];
      t.rows.forEach((r, i) => {
        const type = iType >= 0 ? norm(r[iType]) : 'weight';
        const min = num(r[iMin]);
        const amount = num(r[iAmount]);
        if (min == null || amount == null) return warnings.push(`${name} row ${i + 2}: missing Min or Amount`);
        const code = String((iCode >= 0 && r[iCode]) || `${type.toUpperCase()}_${min}`).trim().toUpperCase().replace(/\s+/g, '_');
        const label = String((iLabel >= 0 && r[iLabel]) || code);
        if (type.startsWith('dim')) dimsTiers.push({ code, label, minIn: min, amount });
        else weightTiers.push({ code, label, minLb: min, mode: iMode >= 0 && norm(r[iMode]) === 'add' ? 'add' : 'replace', amount });
      });
      if (kind === 'customer') {
        patch.accessorials = { weightTiers, dimsTiers };
        summary.push(`${weightTiers.length} weight + ${dimsTiers.length} dims accessorials`);
      } else {
        patch.weightTiers = weightTiers;
        summary.push(`${weightTiers.length} weight tiers`);
      }
    }
  });

  if (!summary.length) throw new Error('No rate card tables found — download the template to see the expected columns.');
  return { patch, summary, warnings };
};

/** Downloads the current card as an editable template. */
export const downloadRateCardTemplate = (kind, card, fileName) => {
  const wb = XLSX.utils.book_new();
  const zones = Object.entries(card.zones || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  if (kind === 'customer') {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Zone', 'Rate', 'Amazon Zone'], ...zones.map(([z, v]) => [Number(z), v.rate ?? '', v.amazonZone ?? ''])]), 'Zones');
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([['FSA', 'Zone'], ...Object.entries(card.fsaZone || {}).sort((a, b) => a[0].localeCompare(b[0]))]),
      'FSA'
    );
    const acc = card.accessorials || {};
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['Type', 'Code', 'Label', 'Min', 'Mode', 'Amount'],
        ...(acc.weightTiers || []).map((t) => ['weight', t.code, t.label, t.minLb, t.mode, t.amount]),
        ...(acc.dimsTiers || []).map((t) => ['dims', t.code, t.label, t.minIn, 'add', t.amount]),
      ]),
      'Accessorials'
    );
  } else {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['Zone', 'Rate'], ...zones.map(([z, v]) => [Number(z), v ?? ''])]), 'Zones');
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([['Type', 'Code', 'Label', 'Min', 'Mode', 'Amount'], ...(card.weightTiers || []).map((t) => ['weight', t.code, t.label, t.minLb, t.mode, t.amount])]),
      'Accessorials'
    );
  }
  XLSX.writeFile(wb, fileName);
};
